/**
 * Map resubmit API data to donor form state.
 */

function field(payload, ...keys) {
  for (const key of keys) {
    const value = payload?.[key]
    if (value !== undefined && value !== null && value !== '') {
      return value
    }
  }
  return ''
}

function parseDateOfBirth(dateOfBirth) {
  const dob = dateOfBirth || '1900-01-01'
  const parts = String(dob).split('-')
  if (parts.length !== 3) {
    return { birthYear: 1900, birthMonth: '01', birthDay: 1 }
  }
  const [year, month, day] = parts
  return {
    birthYear: Number(year),
    birthMonth: String(month).padStart(2, '0'),
    birthDay: Number(day),
  }
}

function splitCardHolderName(cardHolderName, firstName, lastName) {
  const name = (cardHolderName || '').trim()
  if (!name) {
    return { billingNameFirst: firstName || '', billingNameLast: lastName || '' }
  }
  const parts = name.split(/\s+/)
  if (parts.length === 1) {
    return { billingNameFirst: parts[0], billingNameLast: lastName || '' }
  }
  return {
    billingNameFirst: parts[0],
    billingNameLast: parts.slice(1).join(' '),
  }
}

function formatCardExpDate(month, year) {
  const m = String(month || '').padStart(2, '0')
  const y = String(year || '').trim()
  if (!m || !y) return ''
  return `${m} / ${y}`
}

function normalizeFrequencyValue(frequency) {
  if (!frequency) return ''
  return String(frequency).toLowerCase().replace(/_/g, '-')
}

export function attachFrequencyToPaymentPrefill(payment, frequencyOptions = []) {
  if (!payment || !frequencyOptions.length) return payment
  const frequencyId = payment.frequencyId
  const freqValue = normalizeFrequencyValue(payment.sustainingFrequency)
  const levelAutorepeat =
    frequencyOptions.find((f) => f.id === frequencyId) ||
    frequencyOptions.find((f) => String(f.value || '').toLowerCase() === freqValue) ||
    null
  return {
    ...payment,
    levelAutorepeat,
  }
}

/**
 * Prefer server-built form_prefill; fall back to client mapping from raw payload.
 */
export function resolveResubmitFormState(apiResponse, frequencyOptions = []) {
  const formPrefill = apiResponse?.formPrefill || apiResponse?.form_prefill
  if (formPrefill) {
    return {
      donorData: formPrefill.donorData || { donor: formPrefill.contact },
      personalPrefill: formPrefill.personal || {},
      contactPrefill: formPrefill.contact || {},
      paymentPrefill: attachFrequencyToPaymentPrefill(formPrefill.payment || {}, frequencyOptions),
    }
  }
  return buildResubmitState(apiResponse?.payload || {}, frequencyOptions)
}

export function buildResubmitState(payload, frequencyOptions = []) {
  const p = payload || {}
  const firstName = field(p, 'first_name', 'firstName')
  const lastName = field(p, 'last_name', 'lastName')
  const { birthYear, birthMonth, birthDay } = parseDateOfBirth(
    field(p, 'date_of_birth', 'dateOfBirth'),
  )
  const { billingNameFirst, billingNameLast } = splitCardHolderName(
    field(p, 'card_holder_name', 'cardHolderName'),
    firstName,
    lastName,
  )

  const frequencyId = field(p, 'frequency_id', 'frequencyId')
  const freqValue = normalizeFrequencyValue(field(p, 'frequency', 'frequency'))
  const levelAutorepeat =
    frequencyOptions.find((f) => f.id === frequencyId) ||
    frequencyOptions.find((f) => String(f.value || '').toLowerCase() === freqValue) ||
    null

  const donor = {
    firstName,
    lastName,
    dateOfBirth: field(p, 'date_of_birth', 'dateOfBirth') || '1900-01-01',
    email: field(p, 'mail', 'email'),
    mobilePhone: field(p, 'mobile_phone', 'mobilePhone'),
    homePhone: field(p, 'home_phone', 'homePhone') || field(p, 'mobile_phone', 'mobilePhone'),
    addressStreet: field(p, 'address', 'address'),
    addressDetail: field(p, 'address_detail', 'addressDetail'),
    city: field(p, 'city', 'city'),
    state: field(p, 'state', 'state'),
    postalCode: field(p, 'zip', 'postalCode', 'zip'),
    latitude: p.latitude ?? 0,
    longitude: p.longitude ?? 0,
    isEmailPreferred: Boolean(p.is_email_preferred ?? p.isEmailPreferred),
    isSmsPreferred: Boolean(p.is_sms_preferred ?? p.isSmsPreferred),
    isPhoneCallPreferred: Boolean(p.is_phone_call_preferred ?? p.isPhoneCallPreferred),
    emailUpdates: p.email_updates ?? p.emailUpdates ?? 'Y',
    textMessages: p.text_messages ?? p.textMessages ?? 'Y',
    originSource: field(p, 'origin_source', 'originSource'),
  }

  const personalPrefill = {
    firstName: donor.firstName,
    lastName: donor.lastName,
    birthDay,
    birthMonth,
    birthYear,
    latitude: donor.latitude,
    longitude: donor.longitude,
  }

  const contactPrefill = {
    addressStreet: donor.addressStreet,
    addressDetail: donor.addressDetail,
    city: donor.city,
    state: donor.state,
    postalCode: donor.postalCode,
    email: donor.email,
    mobilePhone: donor.mobilePhone,
    homePhone: donor.homePhone,
    isEmailPreferred: donor.isEmailPreferred,
    isSmsPreferred: donor.isSmsPreferred,
    isPhoneCallPreferred: donor.isPhoneCallPreferred,
    emailUpdates: donor.emailUpdates,
    textMessages: donor.textMessages,
    originSource: donor.originSource,
  }

  const paymentPrefill = attachFrequencyToPaymentPrefill(
    {
      uniqueId: field(p, 'unique_id', 'uniqueId'),
      billingNameFirst,
      billingNameLast,
      billingAddressStreet1: field(p, 'address', 'address'),
      billingAddressStreet2: field(p, 'billing_address_street2', 'billingAddressStreet2'),
      billingAddressCity: field(p, 'city', 'city'),
      billingAddressState: field(p, 'state', 'state'),
      billingAddressZip: field(p, 'zip', 'postalCode', 'zip'),
      donorEmail: field(p, 'mail', 'email'),
      cardNumber: field(p, 'card_number', 'cardNumber'),
      cardCvv: field(p, 'card_cvv', 'cardCvv'),
      cardExpDate: formatCardExpDate(
        field(p, 'card_exp_date_month', 'cardExpDateMonth'),
        field(p, 'card_exp_date_year', 'cardExpDateYear'),
      ),
      customAmount: p.amount != null ? String(p.amount) : '',
      frequencyId,
      sustainingFrequency: field(p, 'frequency', 'frequency'),
      donorVerificationSms: Boolean(p.donor_verification_sms ?? p.donorVerificationSms),
    },
    frequencyOptions,
  )

  return {
    donorData: { donor },
    personalPrefill,
    contactPrefill,
    paymentPrefill,
  }
}
